<template>
  <div
    data-testid="checkout-div-39"
    :class="appendCls"
    @click.stop="toggleMenu"
    v-clickoutside="handleClose"
  >
    <zm-scrollbar
      ref="wrapper"
      class="zm-select-scrollbar-fix"
      :wrap-style="inputWrapStyl"
    >
      <div
        ref="reference"
        class="zm-select-input"
        :style="inputStyl"
        :class="{
          'has-slot': $slots.prefix || $slots.post,
          'zm-select--show-label': (selectedLabel || selected.length) && isInlineLabel,
          'is-focused': inlineFocus
        }"
      >
        <label v-if="isInlineLabel" class="zm-select__label" :for="labelFor">{{ inlineLabelText }}</label>
        <div class="zm-select-prefix" v-if="$slots.prefix">
          <slot name="prefix"></slot>
        </div>
        <span
          ref="referenceInner"
          role="button"
          class="zm-select-span__inner"
          :class="{'is-placeholder': selectedLabel===''}"
          :id="targetId"
          :disabled="selectDisabled"
          :aria-expanded="(visible).toString()"
          aria-haspopup="listbox"
          :aria-controls="listId"
          :aria-required="isRequired"
          :aria-disabled="selectDisabled"
          :aria-label="(label ? (label + ',') : '') + (selectedLabel || currentPlaceholder) + (postLabel ? `, ${postLabel}` : '')"
          :aria-describedby="labelBy"
          @keydown="handleBtnKeyDown"
          :tabindex="selectDisabled ? -1 : 0"
          @focus="handleFocus"
          @blur="handleBlur"
        >
          <span>{{normalVal}}</span>
        </span>
        <!-- new feature: slot post -->
        <div class="zm-select-post" v-if="$slots.post">
          <slot name="post"></slot>
        </div>
        <div
          class="zm-select__tags"
          v-if="multiple"
          ref="tags"
          :style="{ 'max-width': inputWidth - 25 + 'px' }"
        >
          <span v-if="collapseTags && selected.length">
            <zm-tag
              :closable="!selectDisabled"
              class="zm-tag-fix"
              :size="collapseTagSize"
              :hit="selected[0].hitState"
              :delete-text="t('tag.delete') + ' ' + selected[0].currentLabel"
              type="info"
              @close="deleteTag($event, selected[0])"
              disable-transitions
            >
              <span class="zm-select__tags-text">{{ selected[0].currentLabel }}</span>
            </zm-tag>
            <zm-tag
              class="zm-tag-fix"
              v-if="selected.length > 1"
              :closable="false"
              :size="collapseTagSize"
              type="info"
              disable-transitions
            >
              <span class="zm-select__tags-text">+ {{ selected.length - 1 }}</span>
            </zm-tag>
          </span>
          <div v-if="!collapseTags">
            <zm-tag
              v-for="item in selected"
              :key="getValueKey(item)"
              :delete-text="t('tag.delete') + ' '+ item.currentLabel"
              :closable="!selectDisabled"
              :size="collapseTagSize"
              :hit="item.hitState"
              :type="item.type || 'info'"
              class="zm-tag-fix"
              @close="deleteTag($event, item)"
              disable-transitions
            >
              <span class="zm-select__tags-text">{{ item.currentLabel }}</span>
            </zm-tag>
          </div>
        </div>
        <button
          data-testid="checkout-button-40"
          class="zm-select-clear"
          v-show="showClear"
          :disabled="selectDisabled"
          type="button"
          @click.stop="clearSelect"
          aria-label="Clear Select"
        >
          <i :class="['zm-select__caret', 'zm-input__icon', 'zm-icon-error','is-show-close']"></i>
        </button>
        <button
          data-testid="checkout-button-41"
          tabindex="-1"
          class="zm-select-toggle"
          type="button"
          aria-hidden="true"
          :disabled="selectDisabled"
          @click.stop="handleIconClick"
          :aria-label="toggleText"
        >
          <i :class="['zm-select__caret', 'zm-input__icon', 'zm-icon-up']"></i>
        </button>
      </div>
    </zm-scrollbar>
    <transition
      name="zm-zoom-in-top"
      @after-enter="handleMenuEnter"
      @after-leave="handleMenuLeave"
      :duration="duration"
    >
      <zm-select-dropdown
        ref="popper"
        :class="{'zm-select-menu-width-limit': widthLimit}"
        :append-to-body="popperAppendToBody"
        :width-limit="widthLimit"
        :popper-options="popperOptions"
        v-show="visible && emptyText!==false"
        @keydown.native="captureTabbableSlotKeydown"
      >
        <zm-scrollbar
          wrap-class="zm-select-dropdown__wrap"
          :wrap-style="wrapStyl"
          ref="scrollbar"
          :class="{ 'is-empty': hasEmptyCls }"
        >
          <template v-if="!$scopedSlots.default">
            <zm-checkbox-list
              v-if="multiple"
              ref="checkboxList"
              v-model="checkListVal"
              :items="checkboxItems"
              :column-count="1"
              :span="24"
              role="application"
              :limit="multipleLimit"
              :id="listId"
              @change="onCheckListChange"
              @keydown="handleCheckboxListKeyDown"
            >
            </zm-checkbox-list>
          </template>
          <template v-else>
            <zm-checkbox-list
              v-if="multiple"
              ref="checkboxList"
              v-model="checkListVal"
              :items="checkboxItems"
              :column-count="1"
              :span="24"
              role="application"
              :limit="multipleLimit"
              :id="listId"
              @change="onCheckListChange"
              @keydown="handleCheckboxListKeyDown"
            >
              <template #default="props">
                <slot :item="props.item">
                </slot>
              </template>
            </zm-checkbox-list>
          </template>
          <dl
            v-if="!multiple"
            role="listbox"
            tabindex="-1"
            :aria-label="listLabel"
            :aria-labelledby="listLabelBy"
            :id="listId"
            class="zm-select-dropdown__list"
            v-show="shouldShowOptions"
            ref="list"
            :aria-activedescendant="visible ? activedescendant: null"
            @keydown="handleListKeyDown"
          >
            <slot></slot>
          </dl>
          <slot name="popper-append"></slot>
        </zm-scrollbar>
        <template v-if="emptyText">
          <slot name="empty" v-if="$slots.empty"></slot>
          <p class="zm-select-dropdown__empty" v-else>
            {{ emptyText }}
          </p>
        </template>
      </zm-select-dropdown>
    </transition>
  </div>
</template>

<script>
import NormalSelect from '@zoom/zoom-ui/src/components/select/normal-select.vue';

export default {
  name: 'opc-normal-select',
  props: {
    postLabel: {
      type: String,
      default: ''
    }
  },
  extends: NormalSelect,
};
</script>

<style lang='scss' scoped>
.zm-select .zm-select-post {
  line-height: 24px;
  height: 32px;
  margin-left: 12px;
}
</style>
